/**
 * Audio framing.
 *
 * Audio travels as WebSocket binary frames rather than base64 inside JSON.
 * Base64 costs a third more bytes and a parse per frame, and at fifty frames a
 * second on a mid-range phone that is real battery for no benefit.
 *
 * Each frame carries an eight byte header. The alternative is to bracket a run
 * of raw frames with control messages and trust that nothing arrives out of
 * order, which works right up until a barge-in: the person starts talking, the
 * assistant's turn is cancelled, and the frames already in flight land after
 * the cancel. Without a turn number on the frame there is no way to tell those
 * apart from the next turn's audio, and the result is a fragment of the
 * abandoned answer played over the top of the new one. Eight bytes on a frame
 * of twenty milliseconds of 16 kHz mono is about one percent.
 */

export const AUDIO_HEADER_BYTES = 8

/** "NA". Lets a frame from something else be rejected rather than decoded. */
const MAGIC_0 = 0x4e
const MAGIC_1 = 0x41

export const AUDIO_FRAME_VERSION = 1

export const AUDIO_KINDS = ['speech'] as const
export type AudioKind = (typeof AUDIO_KINDS)[number]

const KIND_TO_BYTE: Record<AudioKind, number> = { speech: 1 }
const BYTE_TO_KIND: Record<number, AudioKind> = { 1: 'speech' }

/** The formats this protocol allows. Anything else is refused at the handshake. */
export const AUDIO_CODECS = ['pcm16', 'opus'] as const
export type AudioCodec = (typeof AUDIO_CODECS)[number]

/**
 * Sample rates the protocol allows.
 *
 * 16 kHz is the working rate: it is what speech recognisers want and it halves
 * the bytes against 24 kHz on connections where that matters most. 24 kHz is
 * here because several speech synthesisers emit it natively and resampling on
 * a phone to save a little bandwidth is a poor trade against the CPU.
 */
export const SAMPLE_RATES = [8000, 16000, 24000, 48000] as const
export type SampleRate = (typeof SAMPLE_RATES)[number]

export interface AudioFormat {
  codec: AudioCodec
  sampleRate: SampleRate
}

export const DEFAULT_INPUT_FORMAT: AudioFormat = { codec: 'pcm16', sampleRate: 16000 }
export const DEFAULT_OUTPUT_FORMAT: AudioFormat = { codec: 'pcm16', sampleRate: 24000 }

export function isAudioCodec(value: unknown): value is AudioCodec {
  return typeof value === 'string' && (AUDIO_CODECS as readonly string[]).includes(value)
}

export function isSampleRate(value: unknown): value is SampleRate {
  return typeof value === 'number' && (SAMPLE_RATES as readonly number[]).includes(value)
}

export interface AudioFrame {
  kind: AudioKind
  /**
   * Which turn this belongs to. Frames from a turn the receiver has already
   * finished or cancelled are dropped rather than played.
   */
  turn: number
  /**
   * The samples. A view over the received buffer, not a copy: callers that
   * keep it past the current tick must copy it themselves.
   */
  payload: Uint8Array
}

/** The largest payload a single frame may carry, before the header. */
export const MAX_AUDIO_PAYLOAD_BYTES = 64 * 1024

export function encodeAudioFrame(frame: AudioFrame): Uint8Array {
  const { kind, turn, payload } = frame
  if (!Number.isInteger(turn) || turn < 0 || turn > 0xffffffff) {
    throw new RangeError(`turn must fit in an unsigned 32 bit integer, got ${turn}`)
  }
  if (payload.byteLength > MAX_AUDIO_PAYLOAD_BYTES) {
    throw new RangeError(`audio payload is ${payload.byteLength} bytes, over the frame limit`)
  }

  const out = new Uint8Array(AUDIO_HEADER_BYTES + payload.byteLength)
  out[0] = MAGIC_0
  out[1] = MAGIC_1
  out[2] = AUDIO_FRAME_VERSION
  out[3] = KIND_TO_BYTE[kind]
  new DataView(out.buffer).setUint32(4, turn, true)
  out.set(payload, AUDIO_HEADER_BYTES)
  return out
}

export type DecodedAudioFrame =
  | { ok: true; frame: AudioFrame }
  | { ok: false; reason: string }

/**
 * Reads a binary frame off the wire.
 *
 * Returns a reason rather than throwing, because the caller is a message loop
 * handling input from the network and every path through it has to be handled
 * anyway. A throw here would only move the same decision one frame further up.
 */
export function decodeAudioFrame(data: Uint8Array): DecodedAudioFrame {
  if (data.byteLength < AUDIO_HEADER_BYTES) {
    return { ok: false, reason: `frame is ${data.byteLength} bytes, shorter than the header` }
  }
  if (data[0] !== MAGIC_0 || data[1] !== MAGIC_1) {
    return { ok: false, reason: 'frame does not start with the audio marker' }
  }
  if (data[2] !== AUDIO_FRAME_VERSION) {
    return { ok: false, reason: `unsupported audio frame version ${String(data[2])}` }
  }
  const kind = BYTE_TO_KIND[data[3] as number]
  if (!kind) {
    return { ok: false, reason: `unknown audio frame kind ${String(data[3])}` }
  }
  if (data.byteLength - AUDIO_HEADER_BYTES > MAX_AUDIO_PAYLOAD_BYTES) {
    return { ok: false, reason: 'audio payload is over the frame limit' }
  }

  const view = new DataView(data.buffer, data.byteOffset, data.byteLength)
  return {
    ok: true,
    frame: {
      kind,
      turn: view.getUint32(4, true),
      payload: data.subarray(AUDIO_HEADER_BYTES),
    },
  }
}

/**
 * Roughly how many bytes one second of a format takes.
 *
 * Exact for PCM and an estimate for Opus, which is a variable rate codec: a
 * second of silence is a few dozen bytes and a second of speech is thousands.
 * The Opus figure assumes the 24 kbps the server encodes at by default, and it
 * is for budgeting and for progress bars, not for arithmetic that has to be
 * right.
 *
 * Anything that needs an exact duration measures it on the PCM side instead,
 * which is why the server accounts for turn lengths after decoding rather than
 * from bytes received.
 */
export function bytesPerSecond(format: AudioFormat): number {
  if (format.codec === 'opus') return 24_000 / 8
  return format.sampleRate * 2
}

/** How long a payload of this many bytes runs for, in milliseconds. */
export function durationMs(format: AudioFormat, byteLength: number): number {
  return Math.round((byteLength / bytesPerSecond(format)) * 1000)
}
